/**
 * /shouts — the week's shouts, newest first, with the composer at the top.
 *
 * The composer at the top is the same sheet the floating button opens, which
 * is why this screen has a button rather than an inline textarea: one composer
 * means one set of rules about weeks, mentions and images, and no second
 * implementation to keep in step.
 *
 * The week selector matches the one on Timeline and on the picks board, and
 * `?week=` lives in the URL for the same reason it does there.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { Shout, ShoutsPage } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, DEL, GET } from '../lib/api'
import { Avatar, Body, Card, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'
import { ShoutSheet } from '../ui/Shout'
import { WeekSwitcher } from '../ui/WeekSwitcher'

const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: 'short' })

function when(at: number, now: number): string {
  const sameDay = new Date(at).toDateString() === new Date(now).toDateString()
  const clock = HHMM.format(at).replace(/\s?[AP]\.?M\.?$/i, '')
  return sameDay ? clock : `${WEEKDAY.format(at)} ${clock}`
}

/**
 * Renders `@name` in the accent colour, and only for names that actually
 * resolved to somebody. An unmatched "@nobody" stays plain text, which is the
 * honest rendering: highlighting it would imply a person who isn't there.
 */
function Body_({ shout }: { shout: Shout }) {
  if (!shout.mentions.length) return <>{shout.body}</>

  const names = shout.mentions.map((m) => m.displayName)
  // Longest first, so "@mary jane" is consumed before "@mary" can match it.
  names.sort((a, b) => b.length - a.length)
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`@(${escaped.join('|')})`, 'gi')

  const out: React.ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(shout.body)) !== null) {
    if (m.index > last) out.push(shout.body.slice(last, m.index))
    out.push(
      <span key={`${m.index}`} style={{ color: 'var(--bright)', fontWeight: 600 }}>{m[0]}</span>,
    )
    last = m.index + m[0].length
  }
  if (last < shout.body.length) out.push(shout.body.slice(last))
  return <>{out}</>
}

/**
 * The picture, and — when there is a bigger one behind it — a way to get it.
 *
 * `imageUrl` is already the small copy when one exists; `fullImageUrl` is set
 * only when the two differ, which is exactly when a download is worth
 * offering. The control sits in the bottom-right corner of the image rather
 * than under it, so it belongs to the picture and costs the feed no height.
 *
 * A plain `<a download>` rather than a fetch: the file is same-origin and
 * behind the session cookie, and the Worker sends it with a `Content-
 * Disposition: attachment`, so the browser's own download does the right thing
 * without this screen holding 8 MB in memory to hand it over.
 */
function ShoutImage({ shout }: { shout: Shout }) {
  const mb = shout.imageBytes ? shout.imageBytes / (1024 * 1024) : null
  return (
    <div style={{ position: 'relative', marginTop: 10 }}>
      <img
        src={shout.imageUrl ?? ''}
        alt=""
        loading="lazy"
        style={{
          // Capped and `contain` rather than free-height: a phone photo is 4:3
          // at worst and a screenshot can be 9:32, and one of those turns a
          // feed into a scroll wheel.
          width: '100%', maxHeight: 420, objectFit: 'contain',
          background: 'var(--bg)', borderRadius: 12,
          border: '1px solid var(--line)', display: 'block',
        }}
      />
      {shout.fullImageUrl ? (
        <a
          href={shout.fullImageUrl}
          download
          aria-label={mb ? `Download the full image, ${mb.toFixed(1)} MB` : 'Download the full image'}
          className="mono"
          style={{
            position: 'absolute', right: 8, bottom: 8,
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 11px', minHeight: 36, borderRadius: 10,
            // Its own ground rather than a theme token: it sits on a
            // photograph, and no palette colour is legible on every photograph.
            background: 'rgba(0,0,0,.66)', color: '#fff',
            fontSize: 11, fontWeight: 600, textDecoration: 'none',
            backdropFilter: 'blur(4px)',
          }}
        >
          <span aria-hidden>⬇</span>
          {mb ? `${mb.toFixed(1)} MB` : 'Full size'}
        </a>
      ) : null}
    </div>
  )
}

export default function Shouts() {
  const { state, toast } = useStore()
  const [params, setParams] = useSearchParams()
  const weekParam = params.get('week')

  const [page, setPage] = useState<ShoutsPage | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [composing, setComposing] = useState(false)
  const now = Date.now()

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const qs = weekParam ? `?week=${encodeURIComponent(weekParam)}` : ''
      setPage(await GET<ShoutsPage>(`/api/shouts${qs}`, signal))
      setErr(null)
    } catch (e) {
      if (signal?.aborted) return
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load the shouts.')
    }
  }, [weekParam])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  const onWeek = useCallback((id: string) => {
    setPage(null)
    setParams({ week: id }, { replace: true })
  }, [setParams])

  const remove = useCallback(async (id: string) => {
    try {
      await DEL(`/api/shouts/${encodeURIComponent(id)}`)
      setPage((p) => (p ? { ...p, shouts: p.shouts.filter((s) => s.id !== id) } : p))
    } catch (e) {
      toast(e instanceof ApiFailure ? e.message : 'Couldn’t delete that.')
    }
  }, [toast])

  if (state?.user.optedOut) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header bordered={false} title={<span className="screen-title">📣 Shouts</span>} />
        <Body>
          <Card>
            <div style={{ fontSize: 15, fontWeight: 600 }}>You’ve opted out of the community.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
              Nothing was deleted. Turn it back on in Settings and everything is here.
            </div>
            <Link to="/settings" style={{ display: 'inline-block', marginTop: 12, fontSize: 13, fontWeight: 700 }}>
              Settings →
            </Link>
          </Card>
        </Body>
      </Screen>
    )
  }

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span className="screen-title">📣 Shouts</span>}
        sub={
          <div style={{ marginTop: 10 }}>
            <WeekSwitcher
              weeks={page?.weeks ?? []}
              weekId={page?.weekId ?? null}
              onChange={onWeek}
              label={page?.weekLabel}
            />
          </div>
        }
      />

      <Body style={{ padding: '10px var(--gutter) calc(24px + var(--nav-h))' }}>
        <button
          onClick={() => setComposing(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            width: '100%', textAlign: 'left', padding: '15px 16px', minHeight: 48,
            borderRadius: 'var(--radius-btn)', border: '1px solid var(--outline)',
            background: 'var(--surf)', color: 'var(--dim)', fontSize: 15,
          }}
        >
          <span style={{ fontSize: 20, lineHeight: 1, flex: '0 0 auto' }} aria-hidden>📣</span>
          <span style={{ minWidth: 0 }}>
            We’re counting on you to heighten the intellectual discourse.
          </span>
        </button>

        <Spacer h={18} />
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!page ? (
          <><SkeletonCard lines={3} /><Spacer h={12} /><SkeletonCard lines={2} /></>
        ) : page.shouts.length === 0 ? (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Nobody has said anything yet.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              Someone has to go first.
            </div>
          </div>
        ) : (
          page.shouts.map((s) => (
            <div
              key={s.id}
              style={{ display: 'flex', gap: 12, padding: '16px 0', borderBottom: '1px solid var(--line)' }}
            >
              <Avatar initials={s.initials} size={38} self={s.isMine} />
              <div style={{ minWidth: 0, flex: '1 1 0%' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, minWidth: 0 }}>{s.displayName}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--dim)', flex: '0 0 auto' }}>
                    {when(s.createdAt, now)}
                  </span>
                  {s.isMine ? (
                    <button
                      onClick={() => { void remove(s.id) }}
                      aria-label="Delete this shout"
                      style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--dim)', minHeight: 32, flex: '0 0 auto' }}
                    >
                      delete
                    </button>
                  ) : null}
                </div>
                <div style={{ fontSize: 15, marginTop: 4, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                  <Body_ shout={s} />
                </div>
                {s.imageUrl ? <ShoutImage shout={s} /> : null}
              </div>
            </div>
          ))
        )}
      </Body>

      <ShoutSheet
        open={composing}
        onClose={() => setComposing(false)}
        defaultWeekId={page?.weekId}
        onPosted={(shout) => {
          setPage((p) => (p && p.weekId === shout.weekId
            ? { ...p, shouts: [shout, ...p.shouts] }
            : p))
          if (page && page.weekId !== shout.weekId) onWeek(shout.weekId)
        }}
      />
    </Screen>
  )
}
